/**
 * 🧠 LOYIHA AGENTI — doimiy suhbat paneli (backend: services/operatorAgent.js, routes/operator.js; docs/knowledge/190).
 * Bitta loyiha = bitta suhbat: tarix serverda (o'chmaydi), agent Claude Code sessiyasini davom ettiradi, kontekst to'lsa
 * o'zi ixchamlaydi (🗜), xotirasi bor (🧠). Jonli oqim o'z socket'i orqali (`operator:*`, faqat shu foydalanuvchiga).
 * Tasdiq kartalari (merge/prod/publish/server buyrug'i) — ✅ bosilgandagina bajariladi.
 * 📌 Ish rejasi (PlanBar.js) — tepada qadalgan reja; javob ostidagi «📌 Reja qilib qadash», jonli `operator:plan`.
 * 🧵 Bir nechta suhbat (2026-10-30): sarlavhadagi 🧵 — ro'yxat, ➕ yangi, ✏️ nom, 🗄 arxiv; tanlangani loyiha bo'yicha eslab qolinadi
 *    (`kh_agent_thread_<loyiha>`). Har API chaqiruvi `thread` bilan. Vazifa chiplari (📝 🔁 🚀 🎥) xabar bilan `flags` bo'lib ketadi.
 * ⏳ Holat qatori + navbat (2026-11-02, Claude Code'dagidek): yozish maydoni ustida doim ko'rinadigan «🧠 Agent ishlayapti… N s ·
 *    oxirgi qadam · ⏹». Agent band paytida yozilgan xabar keyingi navbatgacha agentga BERILMAYDI — u lentada emas, shu qator ostida
 *    «⏳ navbatda» turadi: ✕ bekor (DELETE /messages/:id — agentga yetib bormaydi), ✏️ / ↑ tahrirlash (navbatdan olib maydonga
 *    qaytaradi). Agent o'qigani 409 — lentada «✓ agent o'qidi». ⏹ dan keyin qolganlari: ▶ Hozir yuborish (POST /resume).
 * ↩ Javob (2026-11-03, Telegram'dagi reply): xabar ostidagi «↩ Javob» → yozuv ustida iqtibos, xabar `reply_to` bilan ketadi;
 *    agent asl xabarni to'liq iqtibos qilib oladi (backend docs/knowledge/190).
 */
import React, { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { toast } from 'react-hot-toast';
import { LiveCallEngine, CallBar, blobToBase64, micErrorText } from './LiveCall';
import PlanBar, { looksLikePlan } from './PlanBar';

const fmtTime = (d) => { try { const x = new Date(new Date(d).getTime() + 5 * 3600_000); return `${String(x.getUTCDate()).padStart(2, '0')}.${String(x.getUTCMonth() + 1).padStart(2, '0')} ${String(x.getUTCHours()).padStart(2, '0')}:${String(x.getUTCMinutes()).padStart(2, '0')}`; } catch (e) { return ''; } };
// ↩ Javob iqtibosi (Telegram'dagi reply kabi): kimning xabari + boshi; bosilsa asl xabarga o'tadi.
const REPLY_WHO = { user: 'Siz', agent: '🧠 Agent', system: '📣 Tizim' };
const REPLY_TO = { user: "O'z xabaringizga javob", agent: '🧠 Agent xabariga javob', system: '📣 Tizim xabariga javob' };
function ReplyQuote({ r, light = false, onJump }) {
  if (!r || !r.id) return null;
  return (
    <button type="button" onClick={() => onJump && onJump(r.id)} title="Asl xabarga o'tish"
      className={`block w-full text-left mb-1 pl-2 pr-1 py-0.5 rounded border-l-2 text-[11px] leading-snug ${light ? 'border-white/70 bg-white/15 hover:bg-white/25 text-white/90' : 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 text-gray-600 dark:text-gray-300'}`}>
      <span className="font-semibold">↩ {REPLY_WHO[r.role] || '🧠 Agent'}{r.at ? ` · ${fmtTime(r.at)}` : ''}</span>
      <span className="line-clamp-2 whitespace-normal break-words" translate="no">{r.text}</span>
    </button>
  );
}
// ⏱ Holat qatori hisoblagichi: 42 s · 3 daq 05 s · 1 soat 02 daq.
const fmtDur = (ms) => {
  const s = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} daq ${String(s % 60).padStart(2, '0')} s`;
  return `${Math.floor(s / 3600)} soat ${String(Math.floor(s / 60) % 60).padStart(2, '0')} daq`;
};

function Inline({ text }) {
  const parts = String(text || '').split(/(`[^`\n]+`|\*\*[^*\n]+\*\*|https?:\/\/[^\s<>()]+)/g);
  return parts.map((p, i) => {
    if (/^`[^`]+`$/.test(p)) return <code key={i} className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[0.85em] break-all" translate="no">{p.slice(1, -1)}</code>;
    if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (/^https?:\/\//.test(p)) {
      // Havola bosiladigan bo'lsin; 🎥 etap videosi (/media/rec_*.mp4) — shu yerning o'zida pleyer.
      const url = p.replace(/[.,;:!?»”'"]+$/, ''), tail = p.slice(url.length);
      const a = <a href={url} target="_blank" rel="noreferrer" className="text-primary-700 dark:text-primary-400 underline break-all" translate="no">{url}</a>;
      if (/\/media\/rec_[\w.-]+\.mp4$/.test(url)) return <React.Fragment key={i}><video controls preload="none" playsInline src={url} className="block my-1 max-h-64 max-w-full rounded bg-black" />{a}{tail}</React.Fragment>;
      return <React.Fragment key={i}>{a}{tail}</React.Fragment>;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}
// Oddiy matn (operator xabari) — qatorlar <br> bilan (MdText izohi: Telegram'ga nusxada qatorlar saqlansin).
function BrText({ text }) {
  const ls = String(text || '').split('\n');
  return ls.map((l, j) => <React.Fragment key={j}>{l}{j < ls.length - 1 ? <br /> : null}</React.Fragment>);
}
// 📋 Xabar matnini xom holida (text/plain) nusxalash — Telegram/hujjatga qatorlari bilan tushadi.
const copyText = async (text) => {
  const t = String(text || '');
  try { await navigator.clipboard.writeText(t); toast.success("📋 Nusxa olindi — Telegram'ga qatorlari bilan tushadi"); }
  catch (e) { window.prompt('Nusxa oling (Ctrl+C):', t); }
};
// 📊 Markdown jadval va sarlavhalar (2026-10-31, operator: «agentning jadvali o'qib bo'lmaydi» — `| a | b |` xom matn bo'lib chiqardi).
const TBL_ROW = /^\s*\|.*\|\s*$/;
const TBL_SEP = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;
const cellsOf = (l) => l.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());
function MdText({ text }) {
  const lines = String(text || '').split('\n');
  const out = [];
  let para = [];
  const flush = () => {
    if (!para.length) return;
    const ls = para; para = [];
    // Qator uzilishi <br> — matnli '\n' emas: belgilab nusxa olganda HTML'da '\n' bo'shliq bo'lib qoladi va Telegram (u text/html ni oladi)
    // ro'yxat/bandlarni bitta qatorga yopishtirardi (operator, 2026-11-03). Sublime text/plain oladi — unda to'g'ri edi.
    out.push(<div key={`p${out.length}`} className="whitespace-pre-wrap break-words">{ls.map((l, j) => <React.Fragment key={j}><Inline text={l} />{j < ls.length - 1 ? <br /> : null}</React.Fragment>)}</div>);
  };
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (TBL_ROW.test(l) && i + 1 < lines.length && TBL_SEP.test(lines[i + 1])) {
      flush();
      const head = cellsOf(l);
      const rows = [];
      i += 2;
      while (i < lines.length && TBL_ROW.test(lines[i])) { rows.push(cellsOf(lines[i])); i++; }
      i--;
      out.push(
        <div key={`t${out.length}`} className="my-1.5 overflow-x-auto">
          <table className="min-w-full text-[0.92em] border-collapse">
            <thead><tr>{head.map((c, k) => <th key={k} className="border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/60 px-2 py-1 text-left font-semibold align-bottom"><Inline text={c} /></th>)}</tr></thead>
            <tbody>{rows.map((r, ri) => <tr key={ri} className="odd:bg-white even:bg-gray-50/60 dark:odd:bg-transparent dark:even:bg-gray-700/20">{head.map((_, k) => <td key={k} className="border border-gray-200 dark:border-gray-600 px-2 py-1 align-top break-words"><Inline text={r[k] || ''} /></td>)}</tr>)}</tbody>
          </table>
        </div>);
      continue;
    }
    const h = /^\s*(#{1,4})\s+(.+)$/.exec(l);
    if (h) { flush(); out.push(<div key={`h${out.length}`} className={`font-semibold mt-1.5 ${h[1].length <= 2 ? 'text-[1.02em]' : ''}`}><Inline text={h[2]} /></div>); continue; }
    para.push(l);
  }
  flush();
  return out;
}
function Rich({ text, onRun = null }) {
  const blocks = String(text || '').split(/```[a-z]*\n?/i);
  return blocks.map((b, i) => (i % 2 === 1
    ? (
      <div key={i} className="relative group my-1.5">
        <pre className="p-2 pr-10 rounded-md bg-gray-900 text-gray-100 text-xs overflow-x-auto whitespace-pre" translate="no">{b.replace(/\n$/, '')}</pre>
        {onRun && b.trim() && (
          <button type="button" onClick={() => onRun(b.replace(/\n$/, '').trim())}
            title="▶ Shu buyruqni loyihaning test konteynerida jonli terminalda ishga tushirish (parol so'ralsa o'zingiz kiritasiz)"
            className="absolute top-1.5 right-1.5 h-7 px-2 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold shadow">▶</button>
        )}
      </div>
    )
    : <div key={i}><MdText text={b} /></div>));
}

function Media({ apiUrl, media }) {
  if (!Array.isArray(media) || !media.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {media.slice(0, 8).map((m, i) => (m.type === 'video'
        ? <video key={i} src={`${apiUrl}${m.url}`} controls className="max-h-56 rounded border border-gray-200 dark:border-gray-700" />
        : <a key={i} href={`${apiUrl}${m.url}`} target="_blank" rel="noreferrer"><img src={`${apiUrl}${m.url}`} alt={m.caption || ''} className="max-h-40 rounded border border-gray-200 dark:border-gray-700" /></a>))}
    </div>
  );
}

const AUDIO_RE = /\.(mp3|wav|ogg|m4a|aac)$/i;   // backend services/attachments.js AUDIO bilan bir xil

function Attachments({ apiUrl, att, onRemove = null, light = false }) {
  if (!att || (!(att.images || []).length && !(att.files || []).length)) return null;
  const chip = light ? 'bg-white/15 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200';
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {(att.images || []).map((im, i) => (
        <span key={`i${i}`} className="relative inline-block">
          <a href={`${apiUrl}${im.url}`} target="_blank" rel="noreferrer"><img src={`${apiUrl}${im.url}`} alt={im.name || ''} className="h-16 w-16 object-cover rounded border border-gray-300 dark:border-gray-600" /></a>
          {onRemove && <button type="button" onClick={() => onRemove('images', i)} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gray-800 text-white text-[10px] leading-5 text-center">✕</button>}
        </span>
      ))}
      {(att.files || []).map((f, i) => {
        // 🎵 Ovoz fayli (mp3 …) — nomi + kichik pleer: to'g'ri fayl biriktirilganini shu yerda eshitib ko'rasiz.
        const audio = AUDIO_RE.test(f.name || f.path || '');
        return (
          <span key={`f${i}`} className={`inline-flex items-center gap-1 ${audio ? 'max-w-[18rem] flex-wrap' : 'max-w-[14rem]'} px-2 py-1 rounded-md text-xs ${chip}`} translate="no">
            <a href={`${apiUrl}${f.path}`} target="_blank" rel="noreferrer" className="truncate">{audio ? '🎵' : '📄'} {f.name}</a>
            {onRemove && <button type="button" onClick={() => onRemove('files', i)} className="shrink-0 opacity-70 hover:opacity-100">✕</button>}
            {audio && <audio controls preload="none" src={`${apiUrl}${f.path}`} className="h-8 w-full max-w-[16rem]" />}
          </span>
        );
      })}
    </div>
  );
}

function Steps({ activity }) {
  const [open, setOpen] = useState(false);
  if (!Array.isArray(activity) || !activity.length) return null;
  return (
    <div className="mt-1.5">
      <button type="button" onClick={() => setOpen(!open)} className="text-[11px] text-gray-500 dark:text-gray-400 hover:underline">
        {open ? '▾' : '▸'} {activity.length} ta qadam
      </button>
      {open && <div className="mt-1 space-y-0.5 text-[11px] text-gray-500 dark:text-gray-400 font-mono break-all" translate="no">{activity.map((a, i) => <div key={i}>{a}</div>)}</div>}
    </div>
  );
}

// 🛠 Server buyrug'i kartasi (routes/operator.js server_exec): «🛠 <server>: buyruq\n$ <buyruq>\n\nNega: <sabab>\n(<nima uchun tasdiq>)».
// Claude'dagidek: avval MA'NOSI (nega), buyruq faqat bosilganda (2026-10-31, operator: «komandalarni faqat ustiga bossam ko'rsat»).
function parseExecSummary(sum) {
  const m = /^(.*?): buyruq\n\$ ([\s\S]*?)\n\nNega: ([\s\S]*)$/.exec(String(sum || ''));
  if (!m) return null;
  const tail = m[3];
  const i = tail.indexOf('\n(');
  const after = i >= 0 ? tail.slice(i + 1) : '';
  const nm = /^\(([^\n]*)\)\s*([\s\S]*)$/.exec(after);
  return { where: m[1].trim(), cmd: m[2].trim(), why: (i >= 0 ? tail.slice(0, i) : tail).trim(), note: nm ? nm[1].trim() : '', rest: (nm ? nm[2] : after).trim() };
}
function ConfirmCard({ c, busyId, onDecide }) {
  const [showCmd, setShowCmd] = useState(false);
  const ex = parseExecSummary(c.summary);
  const pending = c.status === 'pending' && new Date(c.expires_at).getTime() > Date.now();
  const tone = pending ? 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20'
    : c.status === 'done' ? 'border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
      : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800';
  const label = { pending: '⏳ tasdiq kutilmoqda', done: '✅ bajarildi', failed: '❌ bajarilmadi', rejected: '✖ rad etildi', expired: '⌛ muddati o\'tdi', running: '⏳ bajarilmoqda' }[c.status] || c.status;
  return (
    <div className={`rounded-lg border p-3 text-sm ${tone}`}>
      {ex ? (
        <>
          <div className="text-[11px] text-gray-500 dark:text-gray-400" translate="no">{ex.where}</div>
          <div className="mt-0.5 font-medium text-gray-900 dark:text-gray-100 break-words">{ex.why}</div>
          {(ex.note || ex.rest) && <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 whitespace-pre-wrap break-words">{[ex.note, ex.rest].filter(Boolean).join('\n')}</div>}
          <button type="button" onClick={() => setShowCmd(v => !v)} className="mt-1.5 text-xs text-primary-700 dark:text-primary-300 hover:underline">
            {showCmd ? '▾ Buyruqni yashirish' : '▸ Buyruqni ko\'rsatish'}
          </button>
          {showCmd && <pre className="mt-1 p-2 rounded-md bg-gray-900 text-gray-100 text-xs overflow-x-auto whitespace-pre-wrap break-all" translate="no">$ {ex.cmd}</pre>}
        </>
      ) : <div className="whitespace-pre-wrap break-words text-gray-900 dark:text-gray-100" translate="no">{c.summary}</div>}
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        {pending ? (
          <>
            <button type="button" disabled={busyId === c.id} onClick={() => onDecide(c.id, 'approve')}
              className="px-3 py-1.5 rounded-lg bg-primary-700 text-white text-xs font-semibold hover:bg-primary-800 disabled:opacity-60">✅ Tasdiqlash</button>
            <button type="button" disabled={busyId === c.id} onClick={() => onDecide(c.id, 'reject')}
              className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-60">✖ Rad etish</button>
          </>
        ) : <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>}
        {c.result && !pending && <span className="text-xs text-gray-500 dark:text-gray-400 break-all" translate="no">{String(c.result).slice(0, 160)}</span>}
      </div>
    </div>
  );
}

function MemoryPanel({ authFetch, apiUrl, projectId, onClose }) {
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const load = () => authFetch(`${apiUrl}/operator/${projectId}/memories`).then(r => (r.ok ? r.json() : [])).then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  const del = async (m) => {
    if (!window.confirm(`«${m.name}» xotirasi o'chirilsinmi?`)) return;
    const r = await authFetch(`${apiUrl}/operator/${projectId}/memories/${m.id}`, { method: 'DELETE' });
    if (r.ok) load(); else toast.error("O'chirib bo'lmadi");
  };
  const TYPE = { user: '👤 operator', feedback: '📏 ko\'rsatma', project: '📌 loyiha', reference: '🔗 havola' };
  return (
    <div className="absolute inset-0 z-10 flex flex-col bg-white dark:bg-gray-900">
      <div className="shrink-0 px-3 py-2 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
        <span className="text-sm font-semibold text-gray-900 dark:text-white">🧠 Agent xotirasi</span>
        <button type="button" onClick={onClose} className="px-2 py-1 rounded-lg text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">✕</button>
      </div>
      <div className="flex-1 min-h-0 overflow-auto p-3 space-y-2">
        <p className="text-xs text-gray-500 dark:text-gray-400">Agent suhbatlar orasida eslab qoladigan faktlar: sizning ko'rsatmalaringiz, loyiha holati, havolalar. Unga «eslab qol» desangiz shu yerga yozadi; noto'g'risini o'chiring.</p>
        {rows === null ? <div className="text-sm text-gray-500">⏳</div> : !rows.length ? <div className="text-sm text-gray-500">Hozircha bo'sh.</div> : rows.map(m => (
          <div key={m.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-2.5">
            <div className="flex items-start justify-between gap-2">
              <button type="button" onClick={() => setOpenId(openId === m.id ? null : m.id)} className="text-left min-w-0">
                <div className="text-sm font-medium text-gray-900 dark:text-white break-all" translate="no">{m.name}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{TYPE[m.type] || m.type}{m.project_id ? '' : ' · umumiy'} · {fmtTime(m.updated_at)}</div>
                <div className="text-xs text-gray-700 dark:text-gray-300 mt-0.5">{m.description}</div>
              </button>
              <button type="button" onClick={() => del(m)} title="O'chirish" className="shrink-0 px-2 py-1 rounded text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20">🗑</button>
            </div>
            {/* 📊 Xotira matni ham markdown (jadval/sarlavha/`kod`) — ilgari xom matn edi, jadval `| a | b |` bo'lib chiqardi (2026-11-03). */}
            {openId === m.id && <div className="mt-2 text-xs text-gray-800 dark:text-gray-200 break-words border-t border-gray-100 dark:border-gray-800 pt-2" translate="no"><Rich text={m.body} /></div>}
          </div>
        ))}
      </div>
    </div>
  );
}


// 🖥 Jonli terminal — loyihaning TEST konteynerida (backend: services/webTerminal.js, socket `term:*`). Chiqish saqlanmaydi.
// Parol so'ralganda (oxirgi qatorda «password/parol/пароль») kiritish maydoni o'zi yashirin bo'ladi.
const stripAnsi = (t) => String(t).replace(/\x1b\[[0-9;?]*[ -/]*[@-~]/g, '').replace(/\x1b[()][A-Za-z0-9]/g, '').replace(/\r(?!\n)/g, '');
function TerminalModal({ authFetch, apiUrl, token, projectId, initialCmd, onClose }) {
  const [targets, setTargets] = useState(null);
  const [mod, setMod] = useState('');
  const [cmd, setCmd] = useState(initialCmd || '');
  const [out, setOut] = useState('');
  const [sid, setSid] = useState(null);
  const [exitCode, setExitCode] = useState(null);
  const [line, setLine] = useState('');
  const [busy, setBusy] = useState(false);
  const sockRef = useRef(null);
  const sidRef = useRef(null);
  const outRef = useRef(null);
  const lineRef = useRef(null);
  useEffect(() => {
    authFetch(`${apiUrl}/operator/${projectId}/terminal-targets`).then(r => r.json()).then(d => {
      const t = (d && d.targets) || []; setTargets(t); if (t[0]) setMod(String(t[0].moduleId));
    }).catch(() => setTargets([]));
    const s = io(apiUrl || '', { auth: { token } });
    sockRef.current = s;
    s.on('term:data', (d) => { if (d && d.sid === sidRef.current) setOut(o => (o + stripAnsi(d.data)).slice(-60000)); });
    s.on('term:exit', (d) => { if (d && d.sid === sidRef.current) { setExitCode(d.code == null ? 0 : d.code); sidRef.current = null; setSid(null); } });
    return () => { if (sidRef.current) s.emit('term:close', { sid: sidRef.current }); s.disconnect(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight; }, [out]);
  const tail = out.slice(-200);
  const secret = /(password|parol|пароль|passphrase|secret)[^\n]*:\s*$/i.test(tail);
  const run = () => {
    const s = sockRef.current; if (!s || !mod || busy) return;
    setBusy(true); setOut(''); setExitCode(null);
    s.emit('term:open', { projectId, moduleId: Number(mod), cmd, cols: 110, rows: 32 }, (r) => {
      setBusy(false);
      if (!r || !r.ok) { setOut(`⚠️ ${(r && r.error) || 'Terminal ochilmadi'}\n`); return; }
      sidRef.current = r.sid; setSid(r.sid);
      setOut(`$ ${cmd || '(shell)'}   [${r.container}]\n`);
      setTimeout(() => lineRef.current && lineRef.current.focus(), 50);
    });
  };
  const send = (data) => { if (sidRef.current && sockRef.current) sockRef.current.emit('term:input', { sid: sidRef.current, data }); };
  const stop = () => { if (sidRef.current && sockRef.current) sockRef.current.emit('term:close', { sid: sidRef.current }); };
  const submitLine = () => { send(line + '\n'); setLine(''); };   // aks-sadoni (echo) konteyner pty'si qaytaradi; parol echo'siz
  return (
    <div className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-3" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[90vh] flex flex-col rounded-xl bg-gray-900 border border-gray-700 shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="shrink-0 px-3 py-2 flex items-center gap-2 border-b border-gray-700 text-sm text-gray-100">
          <span className="font-semibold">🖥 Terminal — test konteyner</span>
          <select value={mod} onChange={e => setMod(e.target.value)} disabled={!!sid}
            className="ml-auto bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-gray-100">
            {targets === null && <option>⏳</option>}
            {targets && !targets.length && <option value="">ishlab turgan test konteyner yo'q</option>}
            {(targets || []).map(t => <option key={t.moduleId} value={t.moduleId}>{t.name} ({t.type}) · {t.container}</option>)}
          </select>
          <button type="button" onClick={onClose} className="px-2 text-gray-400 hover:text-white text-lg leading-none">✕</button>
        </div>
        <div className="shrink-0 p-2 flex items-center gap-2 border-b border-gray-800">
          <input value={cmd} onChange={e => setCmd(e.target.value)} disabled={!!sid} placeholder="buyruq (bo'sh — interaktiv shell)"
            onKeyDown={e => { if (e.key === 'Enter' && !sid) run(); }}
            className="flex-1 min-w-0 bg-gray-800 border border-gray-600 rounded px-2 py-1.5 text-xs font-mono text-gray-100" translate="no" />
          {sid ? (
            <button type="button" onClick={stop} className="shrink-0 px-3 py-1.5 rounded bg-red-600 hover:bg-red-500 text-white text-xs font-semibold">⏹ To'xtatish</button>
          ) : (
            <button type="button" onClick={run} disabled={busy || !mod} className="shrink-0 px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold disabled:opacity-50">{busy ? '⏳' : '▶ Ishga tushirish'}</button>
          )}
        </div>
        <pre ref={outRef} className="flex-1 min-h-[16rem] overflow-auto p-3 text-xs font-mono text-gray-100 whitespace-pre-wrap break-all" translate="no">{out || "▶ bosing — buyruq tanlangan test konteynerida ishlaydi. Parol so'ralsa pastdagi maydonga kiriting (u suhbatga va logga tushmaydi)."}{exitCode !== null ? `\n[tugadi · kod ${exitCode}]` : ''}</pre>
        <div className="shrink-0 p-2 flex items-center gap-2 border-t border-gray-800">
          <input ref={lineRef} type={secret ? 'password' : 'text'} value={line} onChange={e => setLine(e.target.value)} disabled={!sid} autoComplete="off"
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitLine(); } else if (e.key === 'c' && e.ctrlKey) { e.preventDefault(); send('\x03'); } }}
            placeholder={!sid ? 'terminal yopiq' : secret ? '🔒 parol (yashirin) — Enter' : 'kiritish — Enter (Ctrl+C — to\'xtatish signali)'}
            className="flex-1 min-w-0 bg-gray-800 border border-gray-600 rounded px-2 py-1.5 text-xs font-mono text-gray-100" />
          <button type="button" onClick={submitLine} disabled={!sid} className="shrink-0 px-2 py-1.5 rounded bg-gray-700 text-gray-100 text-xs disabled:opacity-40">⏎</button>
          <button type="button" onClick={() => send('\x03')} disabled={!sid} title="Ctrl+C" className="shrink-0 px-2 py-1.5 rounded bg-gray-700 text-gray-100 text-xs disabled:opacity-40">^C</button>
        </div>
      </div>
    </div>
  );
}

// `draft.send` qoralamalari (`at`) — panel yopilib qayta ochilsa (yangi mount, o'sha prop) ikkinchi marta yuborilmasin.
const _sentDrafts = new Set();

export default function OperatorChat({ authFetch, apiUrl, token, projectId, projectName, active = true, draft = null }) {
  const afRef = useRef(authFetch); afRef.current = authFetch;
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [input, setInput] = useState('');
  // 💡 Keyingi javob taklifi (agent `[[TAKLIF: …]]`, server meta.suggest): maydon bo'sh bo'lsa kulrang matn, Tab — qabul, Esc — yashirish.
  const [sugHidden, setSugHidden] = useState(null);   // yashirilgan taklif xabari id
  const [sending, setSending] = useState(false);
  const [live, setLive] = useState({ text: '', activity: [], media: [] });
  // _rx — holat qabul qilingan brauzer vaqti: hisoblagich = server (now − turnAt) + o'tgan vaqt (soatlar farqi ta'sir qilmaydi).
  const [st, setStRaw] = useState({ busy: false });
  const setSt = (v) => setStRaw(prev => { const n = typeof v === 'function' ? v(prev) : v; return { ...(n || { busy: false }), _rx: n && n.now && n.now !== prev.now ? Date.now() : prev._rx }; });
  const [, setTick] = useState(0);
  const [cancelingId, setCancelingId] = useState(null);
  const [decidingId, setDecidingId] = useState(null);
  const [showMem, setShowMem] = useState(false);
  // 🔔 Chrome bildirishnomasiga ruxsat (agent tasdiq so'raganda App.js `khNotifyConfirm` chiqaradi).
  const [notifPerm, setNotifPerm] = useState(() => { try { return Notification.permission; } catch (e) { return 'unsupported'; } });
  const [termCmd, setTermCmd] = useState(null);         // 🖥 ▶ bosilgan kod bloki (null = terminal yopiq)
  const [older, setOlder] = useState([]);
  const [att, setAtt] = useState({ images: [], files: [] });   // 📎 yuborilmagan biriktirmalar
  const [uploading, setUploading] = useState(0);
  const fileRef = useRef(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const threadRef = useRef(null);
  // 🧵 Tanlangan suhbat (yuborish/yuklash shunga): localStorage'dan, view javobi bilan tasdiqlanadi.
  const lsThreadKey = `kh_agent_thread_${projectId}`;
  const readLs = (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } };
  const writeLs = (k, v) => { try { localStorage.setItem(k, v); } catch (e) { } };
  const selRef = useRef(Number(readLs(lsThreadKey)) || null);
  const tq = (sep = '?') => (selRef.current ? `${sep}thread=${selRef.current}` : '');
  const [threads, setThreads] = useState([]);
  const [threadsOpen, setThreadsOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [unread, setUnread] = useState({});
  const threadsRef = useRef([]); threadsRef.current = threads;
  // 🏷 Vazifa chiplari — agent shu xabardan vazifa yaratsa qo'llanadi (loyiha bo'yicha eslab qolinadi).
  const lsFlagsKey = `kh_agent_flags_${projectId}`;
  const [flags, setFlagsRaw] = useState(() => { try { return JSON.parse(readLs(lsFlagsKey) || '{}') || {}; } catch (e) { return {}; } });
  const setFlag = (k) => setFlagsRaw(f => { const n = { ...f, [k]: !f[k] }; writeLs(lsFlagsKey, JSON.stringify(n)); return n; });
  // ↩ Javob (Telegram'dagi reply): oldingi xabarga (agentniki yoki o'zingizniki) havola — xabar bilan `reply_to` bo'lib ketadi,
  // agent asl xabar matnini iqtibos qilib oladi. { id, role: 'user'|'agent'|'system', text, at }
  const [replyTo, setReplyTo] = useState(null);
  // 📌 Ish rejasi: faol reja (view javobida + socket 'operator:plan') va 📚 rejalar ro'yxati.
  const [plan, setPlan] = useState(null);
  const [planHist, setPlanHist] = useState([]);
  const [flashId, setFlashId] = useState(null);
  const loadPlans = () => afRef.current(`${apiUrl}/operator/${projectId}/plans${tq()}`).then(r => (r.ok ? r.json() : null))
    .then(d => { if (d) { setPlanHist(d.list || []); setPlan(d.active || null); } }).catch(() => { });
  const loadPlansRef = useRef(loadPlans); loadPlansRef.current = loadPlans;
  const endRef = useRef(null);
  const inputRef = useRef(null);
  // ↕ Yozuv maydoni matnga qarab o'zi kengayadi: 2 qatordan oyna balandligining ~45% igacha, undan keyin ichida aylanadi;
  // ⤢ — vaqtincha katta muharrir (~70%). Ilgari doim 2 qator edi — uzun xabar yozish noqulay (operator, 2026-11-03).
  const [inputBig, setInputBig] = useState(false);
  const stickRef = useRef(true);
  const scrollRef = useRef(null);
  // 🎤 / 🔊 / 📞 ovoz (2026-10-28, eski 💬 Suhbat tabidan ko'chdi): nutq → matn → oddiy xabar; javobning boshi ovozda.
  // STT/TTS — Boshqaruvchi quvuri (OpenRouter, pullik), shuning uchun 🔊 default O'CHIQ (Claude javoblari uzun).
  const [rec, setRec] = useState(null);                 // { secs } yozilayotganda
  const [sttBusy, setSttBusy] = useState(false);
  const [speakOn, setSpeakOnRaw] = useState(() => { try { return localStorage.getItem('kh_agent_speak') === '1'; } catch (e) { return false; } });
  const [playing, setPlaying] = useState(false);
  const [call, setCall] = useState(null);               // null | { state, secs, muted }
  const recRef = useRef(null);
  const audioElRef = useRef(null);
  const engineRef = useRef(null);
  const callTimerRef = useRef(null);
  const callChainRef = useRef(Promise.resolve());
  const speakRef = useRef(speakOn); speakRef.current = speakOn;
  const lastSpokenRef = useRef(null);                   // oxirgi ovozlangan (yoki boshlang'ich) agent xabari id'si

  // 🔔 Shu loyihaning BOSHQA suhbatidagi kutilayotgan tasdiq (2026-11-05, Idora #154): karta faqat o'z suhbatida chiziladi, yorliq esa
  // «Tasdiq kerak — Idora» deb miltillardi — operator qaysi suhbatda ekanini topa olmadi. Banner «Ochish» + 🧵 ro'yxatda 🔔.
  const [otherConf, setOtherConf] = useState([]);
  const loadOtherConf = () => afRef.current(`${apiUrl}/conductor/confirmations/pending`).then(r => (r.ok ? r.json() : null))
    .then(d => { if (d && Array.isArray(d.items)) setOtherConf(d.items.filter(c => Number(c.projectId) === Number(projectId) && c.threadKind === 'operator' && c.threadId)); })
    .catch(() => { });
  const loadOtherConfRef = useRef(loadOtherConf); loadOtherConfRef.current = loadOtherConf;
  const load = async () => {
    try {
      const r = await afRef.current(`${apiUrl}/operator/${projectId}${tq()}`);
      const d = await r.json().catch(() => ({}));
      if (r.status === 404 && selRef.current) { selRef.current = null; writeLs(lsThreadKey, ''); return load(); }   // suhbat o'chgan/boshqaniki — oxirgi faolga
      if (!r.ok) { setErr(d.error || 'Xatolik'); return; }
      setErr(null); setData(d); threadRef.current = d.thread && d.thread.id; loadOtherConfRef.current();
      if (d.thread && d.thread.id && selRef.current !== d.thread.id) { selRef.current = d.thread.id; writeLs(lsThreadKey, String(d.thread.id)); }
      if (d.thread && d.thread.id) setUnread(u => (u[d.thread.id] ? { ...u, [d.thread.id]: false } : u));
      if (d.plan !== undefined) setPlan(d.plan || null);
      const lastA = [...(d.messages || [])].reverse().find(m => m.role === 'assistant' && typeof m.id === 'number' && m.text);
      if (lastSpokenRef.current === null) lastSpokenRef.current = lastA ? lastA.id : 0;   // ochilganda eski javob o'qilmaydi
      else if (lastA && lastA.id > lastSpokenRef.current) {
        lastSpokenRef.current = lastA.id;
        if (engineRef.current || speakRef.current) speakText(lastA.text);
      }
      setSt(d.state || { busy: false });
      if (d.state && d.state.busy) setLive(l => ({ ...l, activity: d.state.activity || l.activity, text: l.text || d.state.live || '' }));
    } catch (e) { setErr("Ulanib bo'lmadi"); }
  };
  // 🧵 Suhbatlar ro'yxati (sarlavhadagi tanlagich uchun).
  const loadThreads = (arch = showArchived) => afRef.current(`${apiUrl}/operator/${projectId}/threads${arch ? '?archived=1' : ''}`).then(r => (r.ok ? r.json() : null))
    .then(d => { if (d && Array.isArray(d.threads)) setThreads(d.threads); }).catch(() => { });
  const switchThread = (id) => {
    if (id && Number(id) === Number(threadRef.current)) { setThreadsOpen(false); return; }
    selRef.current = id ? Number(id) : null; writeLs(lsThreadKey, id ? String(id) : '');
    setThreadsOpen(false); setReplyTo(null);
    setData(null); setOlder([]); setLive({ text: '', activity: [], media: [] }); lastSpokenRef.current = null; setPlan(null); setPlanHist([]);
    stickRef.current = true;
    load(); loadPlans();
  };
  const newThread = async () => {
    try {
      const r = await afRef.current(`${apiUrl}/operator/${projectId}/threads`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || "Yaratib bo'lmadi"); return; }
      switchThread(d.thread.id); loadThreads();
      toast.success('🧵 Yangi suhbat — toza kontekst (🧠 xotira umumiy)');
    } catch (e) { toast.error("Ulanib bo'lmadi"); }
  };
  const patchThread = async (t, body) => {
    try {
      const r = await afRef.current(`${apiUrl}/operator/${projectId}/threads/${t.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || "Bo'lmadi"); return; }
      if (body.archived === true && Number(t.id) === Number(threadRef.current)) switchThread(null);
      else if (body.title && Number(t.id) === Number(threadRef.current)) load();
      loadThreads();
    } catch (e) { toast.error("Ulanib bo'lmadi"); }
  };
  const renameThread = (t) => { const v = window.prompt('Suhbat nomi', t.title || ''); if (v && v.trim() && v.trim() !== t.title) patchThread(t, { title: v.trim() }); };
  useEffect(() => {
    selRef.current = Number(readLs(lsThreadKey)) || null;
    try { setFlagsRaw(JSON.parse(readLs(lsFlagsKey) || '{}') || {}); } catch (e) { setFlagsRaw({}); }
    setData(null); setOlder([]); setLive({ text: '', activity: [], media: [] }); lastSpokenRef.current = null; setPlan(null); setPlanHist([]); setThreads([]); setUnread({}); setReplyTo(null);
    load(); loadPlans(); loadThreads(false);
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  // 📡 Navbatchi «🧠 Agentdan so'rash» — savol yozuv maydoniga tushadi (operator tahrirlab yuboradi).
  // 🗺 Xarita «🧠 Agent bilan maslahat» (`draft.send`) — operator matnni xaritada yozgan: joy konteksti va rasmlari bilan
  // DARHOL yuboriladi (suhbat yuklangach, joriy 🧵 suhbatga). Yiqilsa matn va rasmlar yozuv maydoniga qaytadi.
  const pendingDraftRef = useRef(null);
  useEffect(() => {
    if (!draft) return;
    if (draft.send) {
      if ((draft.projectId && String(draft.projectId) !== String(projectId)) || _sentDrafts.has(draft.at)) return;
      _sentDrafts.add(draft.at); pendingDraftRef.current = draft;
      return;
    }
    if (draft.text) { setInput(draft.text); setTimeout(() => inputRef.current && inputRef.current.focus(), 60); }
  }, [draft]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const d = pendingDraftRef.current;
    if (!d || !data || sending) return;
    pendingDraftRef.current = null;
    send(null, d);
  });

  useEffect(() => {
    if (!token) return undefined;
    const s = io(apiUrl || '', { auth: { token } });
    const mine = (d) => d && threadRef.current && Number(d.threadId) === Number(threadRef.current);
    s.on('operator:delta', (d) => {
      if (!mine(d)) return;
      setLive(l => ({
        text: d.text ? (l.text ? `${l.text}\n\n${d.text}` : d.text) : l.text,
        activity: d.activity ? [...l.activity, d.activity].slice(-40) : l.activity,
        media: d.media ? [...l.media, ...d.media].slice(-12) : l.media,
      }));
    });
    s.on('operator:state', (d) => { if (!mine(d)) return; setSt(d); if (!d.busy) { setLive({ text: '', activity: [], media: [] }); load(); } });
    // ⏳ Navbat o'zgardi: agent navbatdagi xabarlarni oldi (yangi navbat) yoki biri bekor qilindi (boshqa oyna/telefondan ham).
    s.on('operator:queue', (d) => { if (mine(d)) load(); });
    s.on('operator:message', (d) => {
      // Operatorning o'z xabari (role:'user') — agent hali ishlayapti, jonli qadamlar o'chmasin.
      if (mine(d)) { if (d.role !== 'user') setLive({ text: '', activity: [], media: [] }); load(); return; }
      // Shu loyihaning boshqa suhbatiga javob keldi — 🧵 tanlagichda belgi.
      if (d && (threadsRef.current || []).some(t => Number(t.id) === Number(d.threadId))) setUnread(u => ({ ...u, [d.threadId]: true }));
    });
    s.on('operator:confirmation', (d) => { if (mine(d)) load(); else loadOtherConfRef.current(); });
    s.on('operator:plan', (d) => { if (mine(d)) { setPlan(d.plan && (d.plan.status === 'active' || d.plan.status === 'done') ? d.plan : null); loadPlansRef.current(); } });
    s.on('conductor:decision', (d) => { if (mine(d)) load(); else loadOtherConfRef.current(); });
    s.on('conductor:message', (d) => { if (mine(d)) load(); });
    return () => { s.disconnect(); };
  }, [apiUrl, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Socket uzilsa ham holat yangilanib tursin (agent ishlayotganda 10 s).
  useEffect(() => {
    if (!st.busy) return undefined;
    const id = setInterval(load, 10_000);
    return () => clearInterval(id);
  }, [st.busy, projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  // 🔔 Boshqa suhbatdagi tasdiq bor ekan — 60 s da yangilab turamiz (muddati o'tgan / boshqa oynada qaror qilingan tushib ketsin).
  useEffect(() => {
    if (!otherConf.length) return undefined;
    const id = setInterval(() => loadOtherConfRef.current(), 60_000);
    return () => clearInterval(id);
  }, [otherConf.length]);
  // ⏱ Holat qatoridagi hisoblagich har soniyada (faqat agent ishlayotganda).
  useEffect(() => {
    if (!st.busy) return undefined;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [st.busy]);

  const msgCount = data ? data.messages.length : 0;
  useEffect(() => { if (stickRef.current && endRef.current) endRef.current.scrollIntoView({ block: 'end' }); }, [msgCount, live.text, live.activity.length, active]);
  const onScroll = () => { const el = scrollRef.current; if (el) stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120; };

  // 📎 Vazifa formasi bilan bir xil yuklash: rasm → /upload (imageUrl), qolgan fayl → /upload-file (path; tur serverda tekshiriladi).
  const uploadFiles = async (list) => {
    const files = Array.from(list || []).filter(Boolean).slice(0, 12);
    if (!files.length) return;
    setUploading(n => n + files.length);
    for (const f of files) {
      try {
        const fd = new FormData();
        if (/^image\//.test(f.type)) {
          fd.append('image', f, f.name || 'rasm.png');
          const r = await afRef.current(`${apiUrl}/upload`, { method: 'POST', body: fd });
          const d = await r.json().catch(() => ({}));
          if (!r.ok || !d.imageUrl) throw new Error(d.error || 'Rasm yuklanmadi');
          setAtt(a => ({ ...a, images: [...a.images, { url: d.imageUrl, name: f.name }] }));
        } else {
          fd.append('file', f, f.name);
          const r = await afRef.current(`${apiUrl}/upload-file`, { method: 'POST', body: fd });
          const d = await r.json().catch(() => ({}));
          if (!r.ok || !d.path) throw new Error(d.error || `${f.name}: yuklanmadi`);
          setAtt(a => ({ ...a, files: [...a.files, { path: d.path, name: d.name || f.name, size: d.size }] }));
        }
      } catch (e) { toast.error(String((e && e.message) || e)); }
      finally { setUploading(n => n - 1); }
    }
  };
  const onPaste = (e) => {
    const items = Array.from((e.clipboardData && e.clipboardData.items) || []).filter(i => i.kind === 'file');
    if (!items.length) return;
    e.preventDefault();
    uploadFiles(items.map(i => i.getAsFile()));
  };
  const onDrop = (e) => { const fl = e.dataTransfer && e.dataTransfer.files; if (fl && fl.length) { e.preventDefault(); uploadFiles(fl); } };
  const removeAtt = (kind, i) => setAtt(a => ({ ...a, [kind]: a[kind].filter((_, j) => j !== i) }));

  // `ext` — tashqaridan tayyor xabar ({text, attachments}, 🗺 xarita): yozuv maydoniga tegmaydi, yiqilsa unga qaytadi.
  const send = async (spoken = null, ext = null) => {
    const fromVoice = typeof spoken === 'string';
    const fromExt = !fromVoice && !!ext;
    const text = fromVoice ? spoken.trim() : fromExt ? String(ext.text || '').trim() : input.trim();
    const extAtt = fromExt && ext.attachments ? { images: ext.attachments.images || [], files: ext.attachments.files || [] } : null;
    const hasAtt = fromExt ? !!(extAtt && (extAtt.images.length || extAtt.files.length)) : !fromVoice && (att.images.length > 0 || att.files.length > 0);
    if ((!text && !hasAtt) || sending || (uploading && !fromExt)) return;
    const sentAtt = hasAtt ? (fromExt ? extAtt : att) : null;
    const rp = fromExt ? null : replyTo;   // ↩ — 🗺 xaritadan kelgan tayyor xabar o'z kontekstiga ega
    setSending(true); if (!fromVoice && !fromExt) { setInput(''); setAtt({ images: [], files: [] }); } if (rp) setReplyTo(null); stickRef.current = true;
    setData(d => (d ? { ...d, messages: [...d.messages, { id: `tmp-${Date.now()}`, role: 'user', text: text || '(biriktirma)', attachments: sentAtt || undefined, replyTo: rp || undefined, at: new Date().toISOString() }] } : d));
    try {
      const fl = (data && data.mode === 'ask') ? {} : Object.keys(flags).filter(k => flags[k]).reduce((o, k) => ({ ...o, [k]: true }), {});
      const r = await afRef.current(`${apiUrl}/operator/${projectId}/chat`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, attachments: sentAtt || undefined, thread: selRef.current || undefined, flags: Object.keys(fl).length ? fl : undefined, reply_to: rp ? rp.id : undefined }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || 'Xatolik'); if (!fromVoice) setInput(text); if (sentAtt) setAtt(sentAtt); if (rp) setReplyTo(rp); return false; }
      setSt(s => ({ ...s, busy: true }));
      return true;
    } catch (e) { toast.error("Ulanib bo'lmadi"); if (!fromVoice) setInput(text); if (sentAtt) setAtt(sentAtt); if (rp) setReplyTo(rp); return false; }
    finally { setSending(false); setTimeout(() => inputRef.current && inputRef.current.focus(), 30); }
  };
  // ── 🔊 javobni o'qish ──
  const stopAudio = () => { const a = audioElRef.current; audioElRef.current = null; if (a) { try { a.pause(); } catch (e) { } } setPlaying(false); };
  const setSpeakOn = (v) => { setSpeakOnRaw(v); try { localStorage.setItem('kh_agent_speak', v ? '1' : '0'); } catch (e) { } if (!v) stopAudio(); };
  const speakText = async (text) => {
    const eng = engineRef.current;
    try {
      const r = await afRef.current(`${apiUrl}/operator/${projectId}/tts`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.audio) { if (r.status !== 400) toast.error(d.error || "Ovoz tayyorlanmadi"); return; }
      if (eng && engineRef.current === eng) { eng.playMp3(d.audio); return; }
      if (!speakRef.current) return;
      stopAudio();
      const a = new Audio(`data:audio/mpeg;base64,${d.audio}`);
      audioElRef.current = a; setPlaying(true);
      a.onended = () => { if (audioElRef.current === a) { audioElRef.current = null; setPlaying(false); } };
      a.play().catch(() => { setPlaying(false); });
    } catch (e) { /* ovoz ixtiyoriy — matn baribir bor */ }
    finally { if (eng && engineRef.current === eng) eng.setBusy(false); }
  };

  // ── 🎤 ovozli xabar: bosing → gapiring → yana bosing ──
  const sttText = async (blob) => {
    const r = await afRef.current(`${apiUrl}/operator/${projectId}/stt`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ audio: await blobToBase64(blob) }) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { if (r.status !== 400 || !engineRef.current) toast.error(d.error || 'Ovoz tanilmadi'); return ''; }
    return String(d.text || '').trim();
  };
  const startRec = async () => {
    if (rec || sttBusy || call) return;
    stopAudio();
    if (!navigator.mediaDevices || !window.MediaRecorder) { toast.error("Bu brauzer ovoz yozishni qo'llamaydi"); return; }
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } }); }
    catch (e) { toast.error(micErrorText(e), { duration: 8000 }); return; }
    const M = window.MediaRecorder;
    const mime = (M.isTypeSupported && ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4', 'audio/webm'].find(t => M.isTypeSupported(t))) || '';
    const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    const rr = { mr, chunks: [], cancel: false, t0: Date.now(), timer: null };
    recRef.current = rr;
    mr.ondataavailable = (e) => { if (e.data && e.data.size) rr.chunks.push(e.data); };
    mr.onstop = async () => {
      clearInterval(rr.timer); stream.getTracks().forEach(t => t.stop()); recRef.current = null; setRec(null);
      if (rr.cancel) return;
      const blob = new Blob(rr.chunks, { type: mr.mimeType || mime || 'audio/webm' });
      if (Date.now() - rr.t0 < 700 || blob.size < 1500) { toast('Juda qisqa — 🎤 ni bosib gapiring, tugagach yana bosing'); return; }
      setSttBusy(true);
      try { const text = await sttText(blob); if (text) await send(`🎤 ${text}`); }
      catch (e) { toast.error("Ulanib bo'lmadi"); }
      finally { setSttBusy(false); }
    };
    mr.start(250);
    setRec({ secs: 0 });
    rr.timer = setInterval(() => { const secs = Math.floor((Date.now() - rr.t0) / 1000); setRec({ secs }); if (secs >= 120) { try { mr.stop(); } catch (e) { } } }, 250);
  };
  const stopRec = (cancel = false) => { const rr = recRef.current; if (!rr) return; rr.cancel = cancel; try { rr.mr.stop(); } catch (e) { } };

  // ── 📞 jonli qo'ng'iroq: gap tugashi → matn → agentga; javob kelganda boshi ovozda o'qiladi ──
  const callTurn = async (blob) => {
    const eng = engineRef.current; if (!eng) return;
    eng.setBusy(true);
    let ok = false;
    try { const text = await sttText(blob); if (text && engineRef.current === eng) ok = await send(`📞 ${text}`); }
    catch (e) { /* tarmoq — keyingi gapda qayta */ }
    if (!ok && engineRef.current === eng) eng.setBusy(false);   // aks holda javob o'qilgach bo'shaydi
  };
  const startCall = async () => {
    if (engineRef.current || rec) return;
    stopAudio();
    const eng = new LiveCallEngine({
      onState: (s) => setCall(c => (c ? { ...c, state: s } : c)),
      onBargeIn: () => { },
      onUtterance: (blob) => { callChainRef.current = callChainRef.current.then(() => callTurn(blob)).catch(() => { }); },
    });
    engineRef.current = eng;
    setCall({ state: 'idle', secs: 0, muted: false });
    try { await eng.start(); }
    catch (e) { engineRef.current = null; setCall(null); toast.error(micErrorText(e), { duration: 8000 }); return; }
    const t0 = Date.now();
    callTimerRef.current = setInterval(() => setCall(c => (c ? { ...c, secs: Math.floor((Date.now() - t0) / 1000) } : c)), 1000);
  };
  const endCall = () => {
    const eng = engineRef.current; engineRef.current = null;
    if (eng) eng.stop();
    clearInterval(callTimerRef.current);
    setCall(null);
  };
  useEffect(() => () => {
    const eng = engineRef.current; engineRef.current = null; if (eng) eng.stop(); clearInterval(callTimerRef.current);
    const rr = recRef.current; if (rr) { try { rr.cancel = true; rr.mr.stop(); } catch (e) { } }
    const a = audioElRef.current; if (a) { try { a.pause(); } catch (e) { } }
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!active && call) endCall(); }, [active]);   // eslint-disable-line react-hooks/exhaustive-deps

  const stop = async () => {
    const r = await afRef.current(`${apiUrl}/operator/${projectId}/stop`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ thread: selRef.current || undefined }) });
    const d = await r.json().catch(() => ({}));
    if (!d.ok) toast.error(d.error || "To'xtatib bo'lmadi");
  };
  // ⏳ Navbatdagi xabar: ✕ bekor qilish / ✏️ tahrirlash (navbatdan olib yozish maydoniga qaytaradi). Agent o'qib ulgurgan bo'lsa — 409.
  const dropQueued = async (m) => {
    if (typeof m.id !== 'number' || cancelingId) return null;
    setCancelingId(m.id);
    try {
      const r = await afRef.current(`${apiUrl}/operator/${projectId}/messages/${m.id}`, { method: 'DELETE' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || "Bekor qilib bo'lmadi", { duration: 6000 }); load(); return null; }
      setData(x => (x ? { ...x, messages: x.messages.filter(y => y.id !== m.id) } : x));
      load();
      return d;
    } catch (e) { toast.error("Ulanib bo'lmadi"); return null; } finally { setCancelingId(null); }
  };
  const cancelQueued = async (m) => { if (await dropQueued(m)) toast.success('✕ Bekor qilindi — xabar agentga yetib bormaydi'); };
  const editQueued = async (m) => {
    const d = await dropQueued(m);
    if (!d) return;
    const a = d.attachments || {};
    const txt = d.text === '(biriktirma)' && ((a.images || []).length || (a.files || []).length) ? '' : String(d.text || '');
    setInput(cur => (cur.trim() ? `${txt}\n\n${cur}` : txt));
    if ((a.images || []).length || (a.files || []).length) setAtt(x => ({ images: [...x.images, ...(a.images || [])], files: [...x.files, ...(a.files || [])] }));
    if (d.replyTo) setReplyTo(d.replyTo);
    setTimeout(() => { try { const el = inputRef.current; el.focus(); el.setSelectionRange(el.value.length, el.value.length); } catch (e) { } }, 30);
    toast("✏️ Navbatdan olindi — tahrirlab qayta yuboring (Enter)");
  };
  const resumeQueue = async () => {
    try {
      const r = await afRef.current(`${apiUrl}/operator/${projectId}/resume`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ thread: selRef.current || undefined }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || 'Xatolik'); return; }
      setSt(s => ({ ...s, busy: true }));
    } catch (e) { toast.error("Ulanib bo'lmadi"); }
  };
  const compact = async () => {
    if (!window.confirm("Suhbat kontekstini hozir ixchamlaymi? Yozishma xulosaga aylanadi, keyingi xabar yangi sessiyada xulosa bilan davom etadi (eski xabarlar o'chmaydi).")) return;
    const t = toast.loading('🗜 Ixchamlanmoqda…');
    const r = await afRef.current(`${apiUrl}/operator/${projectId}/compact`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ thread: selRef.current || undefined }) });
    const d = await r.json().catch(() => ({}));
    toast.dismiss(t);
    if (r.ok && d.ok) { toast.success(`🗜 ${d.summarized} ta xabar xulosaga aylandi`); load(); } else toast.error(d.error || 'Bo\'lmadi');
  };
  const decide = async (id, action) => {
    if (action === 'approve' && !window.confirm('Tasdiqlaysizmi? Bu amal bajariladi.')) return;
    setDecidingId(id);
    try {
      const r = await afRef.current(`${apiUrl}/conductor/confirmations/${id}/${action}`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok && !d.status) toast.error(d.error || 'Xatolik');
      else if (action === 'approve') toast.success(d.ok ? '✅ Bajarildi — agent davom etadi' : `Bajarilmadi: ${d.error || ''}`);
      load();
    } catch (e) { toast.error("Ulanib bo'lmadi"); } finally { setDecidingId(null); }
  };
  // 📌 Agent javobidan reja qadash (bo'lim/qadamlarni server ajratadi).
  const pinFromMsg = async (m) => {
    if (plan && plan.sourceMsgId !== m.id && !window.confirm(`Tepada «${plan.title}» rejasi bor. Yangisini qadaymizmi? Joriysi 📚 Rejalar ro'yxatiga o'tadi (o'chmaydi).`)) return;
    try {
      const r = await afRef.current(`${apiUrl}/operator/${projectId}/plans`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ fromMessageId: m.id, thread: selRef.current || undefined }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || "Qadab bo'lmadi"); return; }
      setPlan(d.plan); loadPlans();
      toast.success(`📌 Qadaldi: ${d.plan.steps.length} ta qadam — tepada`);
    } catch (e) { toast.error("Ulanib bo'lmadi"); }
  };
  // ↗ Qadam belgilangan javobga o'tish (yuklangan bo'lsa).
  const jumpTo = (msgId) => {
    const el = document.getElementById(`opmsg-${msgId}`);
    if (!el) { toast("Bu xabar eskiroq — yuqoridagi «⬆️ Eskiroq xabarlar» bilan yuklang"); return; }
    stickRef.current = false;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setFlashId(msgId); setTimeout(() => setFlashId(null), 2000);
  };
  // ↩ Xabarga javob yozish — yozuv maydoni ustida iqtibos paneli chiqadi (✖ / Esc — bekor).
  const startReply = (m) => {
    if (typeof m.id !== 'number') return;
    const t = String(m.text || '').replace(/\s+/g, ' ').trim();
    setReplyTo({ id: m.id, role: m.role === 'user' ? 'user' : 'agent', text: t.length > 280 ? `${t.slice(0, 280)}…` : t, at: m.at });
    setTimeout(() => { try { inputRef.current.focus(); } catch (e) { } }, 30);
  };
  const loadOlder = async () => {
    const first = [...older, ...(data ? data.messages : [])].find(m => typeof m.id === 'number');
    if (!first) return;
    setLoadingOlder(true);
    try {
      const r = await afRef.current(`${apiUrl}/operator/${projectId}?before=${first.id}&limit=60${tq('&')}`);
      const d = await r.json().catch(() => null);
      if (d && Array.isArray(d.messages)) setOlder(o => [...d.messages, ...o]);
    } finally { setLoadingOlder(false); }
  };
  // ↕ (inputBig izohi) — har o'zgarishda balandlik qayta o'lchanadi; maydon qayta paydo bo'lganda (suhbat yuklangach, 📞 tugagach) ham.
  React.useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const max = Math.max(80, Math.round(window.innerHeight * (inputBig ? 0.7 : 0.45)));
    const want = el.scrollHeight + 2;
    el.style.height = `${inputBig ? max : Math.min(want, max)}px`;
    el.style.overflowY = inputBig || want > max ? 'auto' : 'hidden';
  }, [input, inputBig, !!data, !!call]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!input) setInputBig(false); }, [input]);

  if (err && !data) return <div className="p-4 text-sm text-red-600 dark:text-red-400">{err}</div>;
  if (!data) return <div className="p-4 text-sm text-gray-500 dark:text-gray-400">⏳ Yuklanmoqda…</div>;
  // ⏳ Navbat: server `queued` belgilagan (agentga hali berilmagan) xabarlar; socket holati (givenUpTo) yangiroq bo'lsa — berilgan.
  // Yuborilayotgan (tmp-) xabar agent band bo'lsa ham navbatga. Ular lentada emas, holat qatori ostida — ✕/✏️ bilan.
  const given = Number(st.givenUpTo) || 0;
  const isQueued = (m) => m.role === 'user' && (typeof m.id === 'number' ? !!m.queued && !(given && m.id <= given) : !!st.busy);
  const queue = data.messages.filter(isQueued);
  const turnIds = st.busy && Array.isArray(st.turnIds) ? st.turnIds : [];
  const elapsed = st.busy && st.turnAt && st.now ? (st.now - st.turnAt) + (Date.now() - (st._rx || Date.now())) : null;
  const lastAct = live.activity.length ? live.activity[live.activity.length - 1] : '';
  const msgs = [...older, ...data.messages.filter(m => !isQueued(m))];
  const ctx = data.context || {};
  // 🔎 Savol rejimi (server `mode: 'ask'` — loyiha egasi 👥 Jamoa'da bergan ruxsat): faqat savol-javob — vazifa chiplari,
  // 📌 reja, 🧠 xotira va ▶ terminal yo'q (server ham bermaydi).
  const ask = data.mode === 'ask';
  const ctxTone = ctx.pct >= 65 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400';
  const canOlder = (data.hasMore && !older.length) || (older.length > 0 && older.length % 60 === 0);
  // Kutilayotgan tasdiq — doim pastda (harakat kerak). Bajarilgan/rad etilgan — suhbat ichida O'Z VAQTIDA (so'ralgan payti):
  // ilgari oxirgi 3 tasi har doim eng pastda chizilardi va eski «🛠 buyruq … bajarildi» kartasi yangi javoblar ostida
  // yopishib qolardi (operator, 2026-10-29). Yuklangan eng eski xabardan oldingilari ko'rsatilmaydi.
  const pendingConf = (data.confirmations || []).filter(c => c.status === 'pending');
  const tsOf = (d) => { const t = new Date(d).getTime(); return Number.isFinite(t) ? t : 0; };
  const firstAt = msgs.length ? tsOf(msgs[0].at) : 0;
  const timeline = [
    ...msgs.map((m, i) => ({ k: 'm', at: tsOf(m.at), i, m })),
    ...(data.confirmations || []).filter(c => c.status !== 'pending' && tsOf(c.created_at) >= firstAt)
      .map((c, i) => ({ k: 'c', at: tsOf(c.created_at), i: msgs.length + i, c })),
  ].sort((a, b) => (a.at - b.at) || (a.i - b.i));

  return (
    <div className="relative h-full flex flex-col" onDragOver={e => { if (e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files')) e.preventDefault(); }} onDrop={onDrop}>
      {showMem && <MemoryPanel authFetch={authFetch} apiUrl={apiUrl} projectId={projectId} onClose={() => { setShowMem(false); load(); }} />}
      {termCmd !== null && <TerminalModal authFetch={authFetch} apiUrl={apiUrl} token={token} projectId={projectId} initialCmd={termCmd} onClose={() => setTermCmd(null)} />}
      <div className="shrink-0 px-3 py-1.5 flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 text-xs">
        {/* 🧵 Suhbat tanlagichi — loyihada bir nechta suhbat; har birining o'z konteksti va 📌 rejasi, 🧠 xotira umumiy. */}
        <button type="button" onClick={() => { const o = !threadsOpen; setThreadsOpen(o); if (o) loadThreads(); }}
          title="Suhbatlar: ➕ yangi suhbat (toza kontekst), almashish, nom, arxiv. 🧠 Xotira barcha suhbatlarda umumiy."
          className="min-w-0 flex items-center gap-1 font-semibold text-gray-800 dark:text-gray-100 hover:text-primary-700 dark:hover:text-primary-400">
          <span className="truncate" translate="no">🧵 {(data.thread && data.thread.title) || projectName || `#${projectId}`}</span>
          {Object.keys(unread).some(k => unread[k] && Number(k) !== Number(data.thread && data.thread.id)) && <span className="shrink-0 w-2 h-2 rounded-full bg-emerald-500" title="Boshqa suhbatda yangi javob" />}
          <span className="shrink-0 text-gray-400">{threadsOpen ? '▴' : '▾'}</span>
        </button>
        <button type="button" onClick={newThread} title="➕ Yangi suhbat — toza kontekstdan (eski suhbat saqlanib qoladi)" className="shrink-0 px-1.5 py-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700">➕</button>
        <span className={`ml-auto shrink-0 ${ctxTone}`} title={`Kontekst: ${ctx.tokens || 0} / ${ctx.window || 0} token${ctx.compactions ? ` · ${ctx.compactions} marta ixchamlangan` : ''}. ~72% da o'zi ixchamlanadi.`}>
          {ctx.session ? `kontekst ${ctx.pct || 0}%` : 'yangi sessiya'}{ctx.compactions ? ` · 🗜${ctx.compactions}` : ''}
        </span>
        <button type="button" onClick={compact} disabled={st.busy} title="Kontekstni hozir ixchamlash" className="shrink-0 px-1.5 py-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-40">🗜</button>
        {ask ? (
          <span data-kh="agent-ask-mode" title="Savol rejimi: agent loyiha kodini, test muhitini va vazifalar holatini o'qib javob beradi. Vazifa berish va o'zgartirish — loyiha operatori orqali (agent so'rov matnini tayyorlab beradi)."
            className="shrink-0 px-1.5 py-0.5 rounded border border-sky-300 dark:border-sky-800 text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-900/30 font-medium">🔎 Savol rejimi</span>
        ) : (
          <button type="button" onClick={() => setShowMem(true)} title="Agent xotirasi" className="shrink-0 px-1.5 py-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700">🧠 {data.memories || 0}</button>
        )}
        {typeof Notification !== 'undefined' && notifPerm !== 'granted' && (
          <button type="button" title={notifPerm === 'denied'
            ? "Chrome bildirishnomalari bu sayt uchun bloklangan — manzil satridagi 🔒 → Bildirishnomalar → Ruxsat berish"
            : "Agent tasdiq so'raganda (siz boshqa oynada bo'lsangiz ham) Chrome bildirishnomasi chiqsin — bosing va ruxsat bering"}
            onClick={async () => {
              if (notifPerm === 'denied') { toast("Chrome'da bloklangan: manzil satridagi 🔒 → Bildirishnomalar → Ruxsat berish", { duration: 7000 }); return; }
              try { const r = await Notification.requestPermission(); setNotifPerm(r); if (r === 'granted') toast.success("🔔 Endi agent tasdiq so'raganda Chrome bildirishnomasi chiqadi"); } catch (e) { }
            }}
            className={`shrink-0 px-1.5 py-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${notifPerm === 'denied' ? 'opacity-50' : 'animate-pulse'}`}>🔔</button>
        )}
        <button type="button" onClick={() => (playing ? stopAudio() : setSpeakOn(!speakOn))}
          title={playing ? "Ovozni to'xtatish" : speakOn ? "Agent javobining boshi ovozda o'qiladi (bosib o'chiring — pullik, OpenRouter)" : "Javoblar faqat matn (bosib ovozni yoqing)"}
          className={`shrink-0 px-1.5 py-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${playing ? 'text-emerald-600 dark:text-emerald-400 animate-pulse' : ''}`}>{playing ? '⏹🔊' : speakOn ? '🔊' : '🔇'}</button>
        <button type="button" onClick={() => (call ? endCall() : startCall())} disabled={!call && (!!rec || sttBusy)}
          title={call ? "Qo'ng'iroqni tugatish" : "📞 Jonli qo'ng'iroq — tugmasiz gaplashing: gapingiz agentga yoziladi, javobining boshini ovozda aytadi"}
          className={`shrink-0 px-1.5 py-0.5 rounded disabled:opacity-40 ${call ? 'bg-red-600 text-white' : 'hover:bg-gray-200 dark:hover:bg-gray-700'}`}>{call ? '📴' : '📞'}</button>
      </div>
      {threadsOpen && (
        <div className="shrink-0 max-h-[45%] overflow-auto border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-xs space-y-0.5">
          <div className="flex items-center gap-2 px-1 pb-1">
            <button type="button" onClick={newThread} className="px-2 py-1 rounded bg-primary-700 text-white font-semibold hover:bg-primary-800">➕ Yangi suhbat</button>
            <label className="ml-auto inline-flex items-center gap-1 text-gray-500 dark:text-gray-400 cursor-pointer">
              <input type="checkbox" checked={showArchived} onChange={e => { setShowArchived(e.target.checked); loadThreads(e.target.checked); }} /> 🗄 arxiv ham
            </label>
          </div>
          {!threads.length && <div className="px-1 py-2 text-gray-500">⏳</div>}
          {threads.map(t => {
            const cur = Number(t.id) === Number(data.thread && data.thread.id);
            return (
              <div key={t.id} className={`group flex items-center gap-1.5 rounded px-1.5 py-1 ${cur ? 'bg-primary-50 dark:bg-primary-900/30' : 'hover:bg-gray-100 dark:hover:bg-gray-800'} ${t.archived ? 'opacity-60' : ''}`}>
                <button type="button" onClick={() => switchThread(t.id)} className="min-w-0 flex-1 text-left">
                  <div className="flex items-center gap-1.5">
                    {t.busy && <span className="shrink-0 w-2 h-2 rounded-full bg-primary-500 animate-pulse" title="Agent ishlayapti" />}
                    {unread[t.id] && !cur && <span className="shrink-0 w-2 h-2 rounded-full bg-emerald-500" title="Yangi javob" />}
                    {!cur && otherConf.some(c => Number(c.threadId) === Number(t.id)) && <span className="shrink-0 animate-pulse" title="Bu suhbatda tasdiq kutyapti">🔔</span>}
                    <span className={`truncate ${cur ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-800 dark:text-gray-200'}`} translate="no">{t.title || `#${t.id}`}</span>
                    <span className="shrink-0 text-[10px] text-gray-400 tabular-nums">{t.lastAt ? fmtTime(t.lastAt) : ''}{t.pct ? ` · ${t.pct}%` : ''}{t.compactions ? ` · 🗜${t.compactions}` : ''}</span>
                  </div>
                  {t.lastUser && <div className="truncate text-[10px] text-gray-500 dark:text-gray-400">{t.lastUser}</div>}
                </button>
                <button type="button" onClick={() => renameThread(t)} title="Nomini o'zgartirish" className="shrink-0 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">✏️</button>
                <button type="button" onClick={() => patchThread(t, { archived: !t.archived })} disabled={t.busy && !t.archived}
                  title={t.archived ? 'Arxivdan qaytarish' : "Arxivga qo'yish (o'chmaydi — tarix va reja saqlanadi)"}
                  className="shrink-0 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-20">{t.archived ? '♻' : '🗄'}</button>
              </div>
            );
          })}
        </div>
      )}
      {(() => {
        const curId = Number(data.thread && data.thread.id);
        const other = otherConf.filter(c => Number(c.threadId) !== curId);
        if (!other.length) return null;
        return (
          <div className="shrink-0 px-3 py-1.5 border-b border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 text-xs text-amber-900 dark:text-amber-200 space-y-1">
            {other.slice(0, 3).map(c => (
              <div key={c.id} className="flex items-center gap-2">
                <span className="shrink-0 animate-pulse">🔔</span>
                <span className="min-w-0 flex-1 truncate">Boshqa suhbatda tasdiq kutyapti: <b translate="no">«{c.threadTitle || `#${c.threadId}`}»</b>{c.summary ? <> — <span translate="no">{c.summary}</span></> : null}</span>
                <button type="button" onClick={() => switchThread(c.threadId)} className="shrink-0 px-2 py-0.5 rounded bg-amber-600 hover:bg-amber-700 text-white font-semibold">Ochish</button>
              </div>
            ))}
            {other.length > 3 && <div className="text-[11px] opacity-80">… yana {other.length - 3} ta — 🧵 suhbatlar ro'yxatida 🔔 belgisi bilan</div>}
          </div>
        );
      })()}
      <PlanBar authFetch={authFetch} apiUrl={apiUrl} projectId={projectId} plan={plan} onPlan={setPlan} busy={st.busy}
        onJump={jumpTo} history={planHist} onReloadHistory={loadPlans} threadId={data.thread && data.thread.id} />
      <div ref={scrollRef} onScroll={onScroll} className="flex-1 min-h-0 overflow-auto px-3 py-3 space-y-3">
        {canOlder ? (
          <button type="button" onClick={loadOlder} disabled={loadingOlder} className="w-full py-1.5 text-xs text-gray-500 hover:underline">{loadingOlder ? '⏳' : '⬆️ Eskiroq xabarlar'}</button>
        ) : null}
        {!msgs.length && ask && (
          <div className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
            <p>🔎 Bu — loyiha agentiga savol berish joyi. Agent loyiha kodini, test muhitini, vazifalar va sinovlar holatini o'zi ko'rib javob beradi.</p>
            <p>Vazifa berish va biror narsani o'zgartirish bu yerda yo'q — kerak bo'lsa agent so'rovingizni operatorga yuborish uchun tayyor matn yozib beradi.</p>
            <p className="text-xs text-gray-500">Masalan: «haydovchi ilovasida smena qanday yopiladi?», «#2760 vazifa nima o'zgartirdi?», «test muhitda filial sozlamasi qayerda?», «nega SOS dispetcherga bormayapti?».</p>
          </div>
        )}
        {!msgs.length && !ask && (
          <div className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
            <p>Bu — loyihaning doimiy suhbati. Agent kodni, bazani, serverni va test muhitini o'zi ko'radi; vazifa beradi, tasdiqlatadi, sinatadi, deploy/publish qiladi (xavfli amallar ✅ tugma bilan). Suhbat o'chmaydi — kontekst to'lsa o'zi ixchamlaydi va eslab qoladi.</p>
            <p className="text-xs text-gray-500">Masalan: «loyiha holati qanday?», «test muhit nega 502 beryapti?», «shu xatoni tuzatishga vazifa ber va tugagach sina», «TestFlight'dagi oxirgi build holati».</p>
          </div>
        )}
        {timeline.map(({ k, m, c }) => k === 'c' ? <ConfirmCard key={`c${c.id}`} c={c} busyId={decidingId} onDecide={decide} /> : (m.role === 'user' ? (
          <div key={m.id} id={typeof m.id === 'number' ? `opmsg-${m.id}` : undefined} className="flex justify-end">
            <div className={`max-w-[85%] rounded-2xl rounded-br-sm bg-primary-700 text-white px-3 py-2 text-sm whitespace-pre-wrap break-words transition ${flashId === m.id ? 'ring-2 ring-amber-300' : ''}`}><ReplyQuote r={m.replyTo} light onJump={jumpTo} /><span translate="no"><BrText text={m.text} /></span><Attachments apiUrl={apiUrl} att={m.attachments} light /><div className="mt-1 text-[10px] text-white/70 text-right">
              {typeof m.id === 'number' && <button type="button" onClick={() => startReply(m)} title="Shu xabarga javob yozish (Telegram'dagi reply kabi) — agent aynan shu xabarga bog'lab tushunadi"
                className="mr-1.5 px-1 rounded hover:bg-white/20 hover:text-white">↩ Javob</button>}
              <button type="button" onClick={() => copyText(m.text)} title="Matnni nusxalash (qatorlari bilan — Telegram'ga ham to'g'ri tushadi)"
                className="mr-1.5 px-1 rounded hover:bg-white/20 hover:text-white">📋</button>
              {turnIds.includes(m.id) && <span className="mr-1.5" title="Agent bu xabarni o'qidi va hozir shu ustida ishlayapti — endi bekor qilib bo'lmaydi (⏹ To'xtatish mumkin)">✓ agent o'qidi ·</span>}
              {fmtTime(m.at)}</div></div>
          </div>
        ) : m.role === 'system' || m.decision || m.consoleRun ? (
          <div key={m.id} className={`text-xs text-center px-2 ${m.compact ? 'text-primary-700 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'}`}>{m.text}<span className="ml-1 opacity-60">{fmtTime(m.at)}</span></div>
        ) : (
          <div key={m.id} id={`opmsg-${m.id}`} className="flex">
            <div className={`max-w-[92%] rounded-2xl rounded-bl-sm bg-white dark:bg-gray-800 border px-3 py-2 text-sm text-gray-900 dark:text-gray-100 transition ${flashId === m.id ? 'border-amber-400 ring-2 ring-amber-300' : 'border-gray-200 dark:border-gray-700'}`}>
              {/* translate="no": agent matni interfeys emas — 🌐 tarjimon uzun matnda namunalarni sinab telefonni minutlab qotirardi (2026-11-01) */}
              <div translate="no"><Rich text={m.text} onRun={ask ? null : (c) => setTermCmd(c)} /></div>
              <Media apiUrl={apiUrl} media={m.media} />
              <Steps activity={m.activity} />
              <div className="mt-1 flex items-center gap-2 text-[10px] text-gray-400">
                <span>{fmtTime(m.at)}</span>
                {typeof m.id === 'number' && <button type="button" onClick={() => startReply(m)} title="Shu xabarga javob yozish (Telegram'dagi reply kabi) — vazifa, aniqlik yoki e'tirozingizni agent aynan shu xabarga bog'lab tushunadi"
                  className={`px-1.5 py-0.5 rounded border transition ${replyTo && replyTo.id === m.id ? 'border-primary-500 text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/30' : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>↩ Javob</button>}
                <button type="button" onClick={() => copyText(m.text)} title="Javob matnini nusxalash — qatorlar, ro'yxat va bandlar saqlanadi (Telegram'ga to'g'ri tushadi)"
                  className="px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700">📋 Nusxa</button>
                {!ask && typeof m.id === 'number' && looksLikePlan(m.text) && (plan && plan.sourceMsgId === m.id
                  ? <span className="text-amber-600 dark:text-amber-400">📌 tepada qadalgan</span>
                  : <button type="button" onClick={() => pinFromMsg(m)} title="Shu javobdagi rejani suhbat tepasiga qadash — bo'limlar va raqamlangan qadamlar avtomatik ajratiladi, keyin ▶ / ✅ bilan qadamma-qadam bajarasiz"
                      className="px-1.5 py-0.5 rounded border border-amber-300 dark:border-amber-800 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30">📌 Reja qilib qadash</button>)}
              </div>
            </div>
          </div>
        )))}
        {pendingConf.map(c => <ConfirmCard key={`c${c.id}`} c={c} busyId={decidingId} onDecide={decide} />)}
        {/* Jonli javob (qadamlar + oqim matni). Holat va ⏹ — pastdagi doimiy qatorda (lenta qayerga aylantirilgan bo'lmasin ko'rinadi). */}
        {st.busy && (live.activity.length > 0 || live.text || live.media.length > 0) && (
          <div className="rounded-xl border border-dashed border-primary-300 dark:border-primary-800 bg-primary-50/50 dark:bg-primary-900/10 px-3 py-2 text-sm">
            <div className="text-[11px] font-medium text-primary-700 dark:text-primary-300">🧠 Jonli javob</div>
            {live.activity.length > 0 && <div className="mt-1 space-y-0.5 text-[11px] text-gray-500 dark:text-gray-400 font-mono break-all" translate="no">{live.activity.slice(-6).map((a, i) => <div key={i}>{a}</div>)}</div>}
            {live.text && <div className="mt-2 text-gray-800 dark:text-gray-200" translate="no"><Rich text={live.text.replace(/\[\[\s*TAKLIF[\s\S]*?(\]\]|$)/gi, '').slice(-2500)} /></div>}
            <Media apiUrl={apiUrl} media={live.media} />
          </div>
        )}
        <div ref={endRef} />
      </div>
      {/* ⏳ Doimiy holat qatori + navbat (Claude Code'dagidek) — yozish maydoni ustida, lenta aylantirilganda ham ko'rinadi. */}
      {(st.busy || queue.length > 0) && (
        <div className="shrink-0 border-t border-primary-200 dark:border-primary-900 bg-primary-50 dark:bg-gray-800/80 px-3 py-1.5" data-kh="agent-status">
          {st.busy ? (
            <div className="flex items-center gap-2 text-xs min-w-0">
              <span className="relative shrink-0 flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-primary-600" /></span>
              <span className="shrink-0 font-semibold text-primary-700 dark:text-primary-300">{st.compacting ? '🗜 Kontekst ixchamlanmoqda…' : '🧠 Agent ishlayapti…'}</span>
              {elapsed != null && <span className="shrink-0 tabular-nums text-gray-500 dark:text-gray-400" title="Joriy navbat boshlanganidan beri">{fmtDur(elapsed)}</span>}
              {lastAct && <span className="min-w-0 truncate font-mono text-[11px] text-gray-500 dark:text-gray-400" translate="no" title={lastAct}>· {lastAct}</span>}
              <button type="button" onClick={stop} title="Agentni hozir to'xtatish (joriy ish uziladi; navbatdagi xabarlar agentga berilmay qoladi)"
                className="ml-auto shrink-0 px-2 py-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">⏹ To'xtatish</button>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs">
              <span className="min-w-0 text-amber-700 dark:text-amber-400">⏸ {queue.length} ta xabar agentga berilmagan (agent to'xtatilgan) — keyingi xabaringiz bilan birga boradi</span>
              <button type="button" onClick={resumeQueue} title="Navbatdagi xabarlarni hozir agentga berish"
                className="ml-auto shrink-0 px-2 py-0.5 rounded bg-primary-700 text-white font-semibold hover:bg-primary-800">▶ Hozir yuborish</button>
            </div>
          )}
          {queue.length > 0 && (
            <div className="mt-1.5 max-h-36 overflow-auto space-y-1">
              {st.busy && <div className="text-[11px] text-gray-500 dark:text-gray-400">⏳ Navbatda {queue.length} ta — agent joriy ishidan keyin o'qiydi; o'qimaguncha ✕ bekor yoki ✏️ tahrir qilsa bo'ladi.</div>}
              {queue.map(m => (
                <div key={m.id} className="flex items-start gap-1.5 rounded-lg border border-dashed border-primary-300 dark:border-primary-800 bg-white dark:bg-gray-800 px-2 py-1 text-xs" data-kh="queued-msg">
                  <span className="shrink-0" title="Navbatda — agent hali o'qimagan">⏳</span>
                  <span className="min-w-0 flex-1 text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words line-clamp-2" translate="no" title={m.text}>
                    {m.replyTo && m.replyTo.id ? <span className="text-primary-700 dark:text-primary-300" title={`↩ ${m.replyTo.text || ''}`}>↩ </span> : null}{m.text}{m.attachments && ((m.attachments.images || []).length + (m.attachments.files || []).length) > 0 ? ` 📎${(m.attachments.images || []).length + (m.attachments.files || []).length}` : ''}
                  </span>
                  {typeof m.id === 'number' ? (
                    <>
                      <button type="button" onClick={() => editQueued(m)} disabled={!!cancelingId} title="Tahrirlash — navbatdan olib yozish maydoniga qaytaradi (agentga bormaydi), o'zgartirib qayta yuborasiz. Klaviaturada: bo'sh maydonda ↑"
                        className="shrink-0 px-1 rounded text-gray-500 hover:text-primary-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40">✏️</button>
                      <button type="button" onClick={() => cancelQueued(m)} disabled={!!cancelingId} title="Bekor qilish — agent bu xabarni o'qimaydi"
                        className="shrink-0 px-1 rounded text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-40">{cancelingId === m.id ? '⏳' : '✕'}</button>
                    </>
                  ) : <span className="shrink-0 text-[10px] text-gray-400">yuborilmoqda…</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="shrink-0 p-2 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        {(att.images.length > 0 || att.files.length > 0 || uploading > 0) && (
          <div className="mb-1.5 flex items-center gap-2 flex-wrap">
            <Attachments apiUrl={apiUrl} att={att} onRemove={removeAtt} />
            {uploading > 0 && <span className="text-xs text-gray-500 dark:text-gray-400 animate-pulse">⏳ yuklanmoqda ({uploading})…</span>}
          </div>
        )}
        {call ? (
          <div className="px-1 py-1">
            <CallBar state={call.state} engine={engineRef.current} secs={call.secs} muted={call.muted}
              note={st.busy ? '🧠 agent ishlayapti' : null}
              onMute={() => { const m = !call.muted; if (engineRef.current) engineRef.current.setMuted(m); setCall(c => ({ ...c, muted: m })); }}
              onInterrupt={() => { if (engineRef.current) engineRef.current.stopPlayback(); }}
              onHangup={endCall} />
            <div className="text-[10px] text-gray-400 mt-1.5">Gapingiz matn bo'lib agentga ketadi; javob tayyor bo'lgach boshini ovozda aytadi (to'liq matn suhbatda). Quloqchin bilan yaxshiroq. Parol/kalitni aytmang.</div>
          </div>
        ) : (
        <>
        {replyTo && (
          <div className="mb-1.5 flex items-start gap-2 rounded-lg border-l-4 border-primary-500 bg-primary-50 dark:bg-primary-900/20 px-2 py-1 text-xs" data-kh="reply-bar">
            <button type="button" onClick={() => jumpTo(replyTo.id)} title="Asl xabarga o'tish" className="min-w-0 flex-1 text-left">
              <span className="font-semibold text-primary-700 dark:text-primary-300">↩ {REPLY_TO[replyTo.role] || REPLY_TO.agent}{replyTo.at ? ` · ${fmtTime(replyTo.at)}` : ''}</span>
              <span className="block truncate text-gray-600 dark:text-gray-300" translate="no">{replyTo.text}</span>
            </button>
            <button type="button" onClick={() => setReplyTo(null)} title="Javobni bekor qilish (Esc)" className="shrink-0 px-1 rounded text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30">✖</button>
          </div>
        )}
        {/* 🏷 Vazifa chiplari — agent shu xabardan vazifa yaratsa qo'llanadi (vazifa formasidagi chiplarning o'rni). 🔎 savol rejimida yo'q. */}
        {!ask && <div className="mb-1.5 flex items-center gap-1 flex-wrap text-[11px]">
          <span className="text-gray-400 mr-0.5" title="Agent shu xabar bo'yicha vazifa yaratsa — yoqilgan sozlamalar bilan">Vazifa bo'lsa:</span>
          {[['plan_review', "📝 Reja tasdig'i", "Kod yozishdan OLDIN reja ko'rsatiladi — ✅ tasdiqlaguningizcha ish boshlanmaydi"],
            ['after_test', '🔁 Tugagach sina', "Tugagach O'ZI tasdiqlaydi (merge + test deploy/APK), Nazoratchi qurilmada/brauzerda sinaydi, kamchilikni tuzatib qayta sinaydi"],
            ['auto_deploy', '🚀 Avto-deploy', "Tugagach review branch o'zi merge qilinib test muhitga chiqadi — ✅ bosish shart emas"]].map(([k, label, tip]) => (
            <button key={k} type="button" onClick={() => setFlag(k)} title={tip}
              className={`px-2 py-0.5 rounded-md border font-medium transition ${flags[k] ? 'bg-primary-700 dark:bg-primary-600 border-primary-700 text-white' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
              {label}{flags[k] ? ' ✓' : ''}
            </button>
          ))}
        </div>}
        <div className="flex items-end gap-2">
          <input ref={fileRef} type="file" multiple className="hidden"
            accept="image/*,.html,.htm,.pdf,.docx,.pptx,.md,.txt,.csv,.json,.svg,.xml,.yml,.yaml,.mp3,.wav,.ogg,.m4a,.aac"
            onChange={e => { uploadFiles(e.target.files); e.target.value = ''; }} />
          <button type="button" onClick={() => fileRef.current && fileRef.current.click()} title="Rasm yoki fayl biriktirish (skrinshot, dizayn, HTML/PDF/hujjat, 🎵 ovoz — mp3/wav/ogg/m4a) — Ctrl+V bilan qo'yish yoki sudrab tashlash ham mumkin"
            className="shrink-0 h-10 w-10 rounded-lg border border-gray-300 dark:border-gray-600 text-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">📎</button>
          {(() => {
            const lastMsg = data && data.messages.length ? data.messages[data.messages.length - 1] : null;
            const sug = !st.busy && !input && lastMsg && lastMsg.role === 'assistant' && lastMsg.suggest && sugHidden !== lastMsg.id ? lastMsg.suggest : '';
            const take = () => { setInput(sug); setTimeout(() => { try { const el = inputRef.current; el.focus(); el.setSelectionRange(sug.length, sug.length); } catch (e) { } }, 0); };
            return (
              <div className="relative flex-1 min-w-0">
                <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)} rows={2} onPaste={onPaste}
                  onKeyDown={e => {
                    if (sug && (e.key === 'Tab' || (e.key === 'ArrowRight' && !e.shiftKey))) { e.preventDefault(); take(); return; }
                    if (sug && e.key === 'Escape') { e.preventDefault(); setSugHidden(lastMsg.id); return; }
                    if (replyTo && e.key === 'Escape') { e.preventDefault(); setReplyTo(null); return; }
                    // ↑ bo'sh maydonda — oxirgi navbatdagi xabarni tahrirlashga olish (Claude Code'dagidek).
                    if (e.key === 'ArrowUp' && !input && !att.images.length && !att.files.length) {
                      const lastQ = [...queue].reverse().find(q => typeof q.id === 'number');
                      if (lastQ) { e.preventDefault(); editQueued(lastQ); return; }
                    }
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                  }}
                  placeholder={sug ? '' : replyTo ? "Shu xabarga javob — vazifa, aniqlik yoki savolingizni yozing (Esc — bekor)" : st.busy ? "Agent ishlayapti — yozavering, xabar navbatga tushadi" : 'Agentga yozing… 📎 rasm/fayl: tugma, Ctrl+V yoki sudrab tashlang'}
                  className={`w-full block resize-none rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-sm text-gray-900 dark:text-white p-2 focus:ring-primary-500 focus:border-primary-500 ${input.length > 160 || input.split('\n').length > 3 ? 'pr-8' : ''}`} />
                {(input.length > 160 || input.split('\n').length > 3 || inputBig) && (
                  <button type="button" onClick={() => { setInputBig(v => !v); setTimeout(() => { try { inputRef.current.focus(); } catch (e) { } }, 0); }}
                    title={inputBig ? "Odatiy o'lchamga qaytarish" : "Katta muharrir — yozuv maydonini oyna balandligining ~70% igacha kattalashtirish"}
                    className="absolute right-1.5 top-1.5 h-6 w-6 rounded border border-gray-300 dark:border-gray-600 bg-white/90 dark:bg-gray-700/90 text-xs text-gray-500 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-600">{inputBig ? '⤡' : '⤢'}</button>
                )}
                {sug && (
                  <>
                    <div className="absolute inset-[1px] p-2 pr-24 text-sm text-gray-400 dark:text-gray-500 pointer-events-none overflow-hidden whitespace-pre-wrap break-words" aria-hidden="true">{sug}</div>
                    <button type="button" onClick={take} title="Agent taklif qilgan javob — Tab (yoki →) bilan qabul qiling, Esc — yashirish; qabul qilgach o'zgartirib yuborishingiz mumkin"
                      className="absolute right-1.5 bottom-1.5 px-1.5 py-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white/80 dark:bg-gray-700/80 text-[10px] text-gray-500 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-600">↹ Tab — qabul</button>
                  </>
                )}
              </div>
            );
          })()}
          {rec ? (
            <>
              <button type="button" onClick={() => stopRec(false)} title="Tugatib yuborish"
                className="shrink-0 h-10 px-3 rounded-lg bg-red-600 text-white text-sm font-semibold animate-pulse">⏹ {rec.secs}s</button>
              <button type="button" onClick={() => stopRec(true)} title="Bekor qilish" className="shrink-0 h-10 px-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 text-sm">✖</button>
            </>
          ) : (
            <button type="button" onClick={startRec} disabled={sttBusy || sending}
              title="🎤 Ovozli xabar — bosing, gapiring, tugagach yana bosing (2 daqiqagacha). Matnga aylanib agentga yuboriladi."
              className="shrink-0 h-10 w-11 rounded-lg border border-gray-300 dark:border-gray-600 text-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50">{sttBusy ? '⏳' : '🎤'}</button>
          )}
          <button type="button" onClick={() => send()} disabled={sending || uploading > 0 || (!input.trim() && !att.images.length && !att.files.length)}
            className="shrink-0 h-10 px-4 rounded-lg bg-primary-700 text-white text-sm font-semibold hover:bg-primary-800 disabled:opacity-50">Yuborish</button>
        </div>
        </>
        )}
      </div>
    </div>
  );
}
